import { useMemo, useState } from 'react';
import { Loader2, Plus, Users } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import EmptyView from '@/shared/components/EmptyView';
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHead } from '@/shared/ui/table';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/shared/ui/dropdown-menu';
import { MoreVertical, Pencil, Trash } from 'lucide-react';
import { User } from '@/shared/types/userTypes';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { Card, CardContent } from '@/shared/ui/card';
import SearchBar from '@/shared/components/SearchBar';
import { getRoleName, OrgType, Role } from '@/shared/types/userRoleTypes';
import { useCustomerUsers, useRemoveCustomerUser } from '../../services/account-customers';
import { Customer } from '@/shared/types/customerTypes';
import CreateCustomerUserDialog from '@/domains/users/dialogs/CreateCustomerUserDialog';
import ManageCustomerUserDialog from '@/domains/users/dialogs/ManageCustomerUserDialog';
import { useUserRoles } from '@/shared/hooks/userRoles';
import { usePermissions } from '@/shared/hooks/use-permissions';

interface CustomerUsersSectionProps {
  customer: Customer;
  currentUser: User;
}

export const CustomerUsersSection = ({ customer, currentUser }: CustomerUsersSectionProps) => {
  const { hasPermission } = usePermissions();
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [isManageUserDialogOpen, setIsManageUserDialogOpen] = useState(false);

  // Selected User
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Remove User Dialog states
  const [isRemoveUserDialogOpen, setIsRemoveUserDialogOpen] = useState(false);
  const removeCustomerUser = useRemoveCustomerUser();

  // Fetch account users data
  const { data: users, isLoading: isLoadingUsers, error: usersError, refetch: refetchUsers } = useCustomerUsers(customer.customerId);

  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = useMemo(() => {
    return users?.filter(user =>
      user.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
  }, [users, searchTerm]);

  const handleAddUser = () => {
    setIsAddUserDialogOpen(true);
  };

  const onManageUser = (user: User) => {
    setSelectedUser(user);
    setIsManageUserDialogOpen(true);
  };

  // Remove User
  const onRemoveUser = (user: User) => {
    setSelectedUser(user);
    setIsRemoveUserDialogOpen(true);
  };

  const handleConfirmRemoveUser = async () => {
    if (!selectedUser) return;
    try {
      await removeCustomerUser.mutateAsync({ customerId: customer.customerId, userId: selectedUser.userId });
      showSuccess(SUCCESS.USER.REMOVED);
      refetchUsers();
    } catch (error) {
      showErrorWithApiError(error, ERROR.USER.REMOVE);
    }
    setIsRemoveUserDialogOpen(false);
    setSelectedUser(null);
  };

  const canManageUsers = hasPermission(OrgType.CUSTOMER, Feature.USERS, Permission.MANAGE) && customer.isSelfServiceAllowed;
  const { isCustomerUser } = useUserRoles();

  return (
    <div key="users" className="space-y-4" >
      {/* Users Section Header */}
      {
        isLoadingUsers ? (
          <div className="flex justify-center items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Loading users...</p>
          </div>
        ) : (
          <>
            <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
              <div>
                <h2 className="text-2xl font-bold">Users</h2>
                <span className="text-sm">Manage users and their account roles under parent account</span>
              </div>
              {canManageUsers && (
                <Button
                  variant="default"
                  className="gap-2"
                  onClick={handleAddUser}
                  disabled={isLoadingUsers}
                >
                  <Plus className="h-4 w-4" />
                  Add User
                </Button>
              )}
            </div>

            {/* Users Content */}
            {users && users.length > 0 && !usersError ? (
              <div className="space-y-4">
                {/* Search Section */}
                <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Search users by ID, name, or email..." />

                {/* Users Table */}
                {filteredUsers && filteredUsers.length > 0 ? (
                  <Card>
                    <CardContent className="p-0">
                      <Table key="users-table" className="rounded-lg overflow-hidden">
                        <TableHeader className="rounded-lg">
                          <TableRow className="bg-muted">
                            <TableHead className="text-left py-3 px-4 font-medium font-bold">Name</TableHead>
                            <TableHead className="text-left py-3 px-4 font-medium font-bold">Email</TableHead>
                            <TableHead className="text-left py-3 px-4 font-medium font-bold">Role</TableHead>
                            {canManageUsers && <TableHead className="text-right w-[50px] font-medium font-bold"></TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredUsers.map((user, userIndex) => {
                            const hasPermissionToManageCustomerUser = canManageUsers && user.userId != currentUser?.userId && (isCustomerUser ? user.role !== Role.ADMIN : true);
                            return (
                              <TableRow key={user.userId ?? `user-${userIndex}`} className="border-b hover:bg-muted/50">
                                <TableCell className="py-4">
                                  <span className="text-sm font-medium">{user.name}</span>
                                </TableCell>
                                <TableCell className="py-4">
                                  <span className="text-sm font-medium">{user.email}</span>
                                </TableCell>
                                <TableCell className="py-4">
                                  <span className="text-sm font-medium">{getRoleName(user.role)}</span>
                                </TableCell>
                                {hasPermissionToManageCustomerUser && (
                                  <TableCell className="py-4 text-right">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                          <MoreVertical className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {onManageUser && (
                                          <DropdownMenuItem onClick={(e) => {
                                            e.stopPropagation();
                                            onManageUser(user);
                                          }}>
                                            <Pencil className="mr-2 h-4 w-4" />
                                            Manage
                                          </DropdownMenuItem>
                                        )}
                                        {onRemoveUser && (
                                          <DropdownMenuItem
                                            className="text-destructive"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onRemoveUser(user);
                                            }}
                                          >
                                            <Trash className="mr-2 h-4 w-4" />
                                            Remove
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </TableCell>
                                )}
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ) : (
                  <EmptyView
                    icon={Users}
                    title="No Users Found"
                    description="There are no users available."
                  />
                )}
              </div>
            ) : (
              <EmptyView
                icon={Users}
                title="No Users Found"
                description="There are no users available."
              />
            )}
          </>
        )
      }

      {/* Add User Dialog */}
      <CreateCustomerUserDialog
        isOpen={isAddUserDialogOpen}
        onClose={() => {
          setIsAddUserDialogOpen(false);
          refetchUsers();
        }}
        customerId={customer.customerId}
      />

      {/* User Approval Dialog */}
      <ManageCustomerUserDialog
        isOpen={isManageUserDialogOpen}
        onClose={() => {
          setIsManageUserDialogOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={() => refetchUsers()}
        user={selectedUser}
        customerId={customer.customerId}
      />

      {/* Remove User Dialog */}
      <GenericAlertDialog
        open={isRemoveUserDialogOpen}
        onOpenChange={setIsRemoveUserDialogOpen}
        title="Remove User"
        description="Are you sure you want to remove this user from this account?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleConfirmRemoveUser}
        loading={removeCustomerUser.isPending}
      />
    </div>
  );
}
